import type { OfflineSyncItem } from '@/tauri/offline-recording/offline-recording';

/**
 * The subset of an `offline_recording_list` bridge row the sync-queue atom
 * needs. Deliberately structural rather than the bridge's own DTO: the mapping
 * is the contract, and a widened bridge row should not force a change here.
 */
export interface OfflineSyncQueueBridgeRow {
  localSessionId: string;
  serverSessionId: string | null;
  patientLink?: string | null;
  status: string;
  attemptCount: number;
  fileSizeBytes: number | null;
  recordedDurationMs: number | null;
  captureRegion: string;
  createdAt: string;
  createdTimeLocal?: string | null;
  lastErrorCode: string | null;
  recordingCount?: number;
  /**
   * The persisted transcribe/dictate mode. A plain string, as the bridge row
   * gives it: normalising it into the schema enum is
   * `toStoredTranscribeMode`'s job, at the point of display.
   */
  dictationType?: string | null;
  /**
   * The persisted patient-consent answer. `null` is the never-asked column;
   * `false` is a refusal and must not be folded into it.
   *
   * Optional HERE while `OfflineRecordingItem.isConsent` is required, which is
   * the same deliberate gap `dictationType` has and not an oversight — but the
   * gap has a cost worth stating, because it is invisible at this line. The one
   * production caller maps `offlineRecordingBridge.list()` output, so TypeScript
   * already proves the field is present there; a TEST, though, can build a row
   * off this permissive shape, omit `isConsent`, and pass while the mapping is
   * broken. `maps a full bridge list row, DTO-typed` in the sibling test file
   * closes that by running one fixture typed as the required DTO, so a rename on
   * the Rust/DTO side fails to compile instead of reading as never-asked.
   */
  isConsent?: boolean | null;
}

/**
 * Maps a bridge list row onto the shape the offline sync-queue atom holds.
 *
 * Lives in its own module rather than inside the queue's owner so the mapping
 * stays nameable and testable on its own: it is the contract between a durable
 * Rust row and the one atom every offline surface reads, and a drifted second
 * copy of it would write a differently-shaped row into that atom. Only the
 * queue's owner calls it — surfaces that need the queue re-read ask for a
 * coordinated refresh (see `./offline-sync-queue-refresh`) rather than mapping
 * and writing rows themselves.
 */
export function toSyncItem(item: OfflineSyncQueueBridgeRow): OfflineSyncItem {
  return {
    localSessionId: item.localSessionId,
    userId: '',
    serverSessionId: item.serverSessionId,
    localEncryptedPath: '',
    status: item.status as OfflineSyncItem['status'],
    attemptCount: item.attemptCount,
    lastAttemptAt: null,
    lastErrorCode: item.lastErrorCode,
    fileSizeBytes: item.fileSizeBytes,
    recordedDurationMs: item.recordedDurationMs,
    captureRegion: item.captureRegion,
    createdAt: item.createdTimeLocal ?? item.createdAt,
    updatedAt: item.createdAt,
    createdTimeLocal: item.createdTimeLocal,
    recordingCount: item.recordingCount,
    // Carried, not dropped. For a consult that already has a server session
    // this row is the only surviving record of the mode after a quit: the
    // cold-boot detail stub that restores the pickers for a purely LOCAL
    // consult is deliberately not written over a server-backed one, and offline
    // the server query has nothing to return. Losing it here is what makes a
    // dictate consult come back displayed as a transcript one.
    dictationType: item.dictationType,
    // Same argument, for the consent gate's answer: a consult with a server
    // session gets no cold-boot detail stub, so offline this row is the only
    // place the answer survives. Which consults actually arrive with one is
    // narrower than "all of them", and the line splits on WHEN the gate was
    // answered rather than on what the row looks like now. The column's one
    // writer is gated on `isOfflineLocalSession`, so it holds a value only if
    // the clinician answered while the consult was still local-only — which
    // includes a local-born consult that has since been given a server id by
    // sync, and that is the population this mapping carries. A consult that was
    // server-backed from birth never reaches that writer, so its column stays
    // NULL and this arrives `null`; feeding that shape is a separate change on
    // the mutation that owns the write. Copied verbatim either way — folding
    // `false` into the never-asked case here is the one mapping that would
    // settle a clinical question by accident.
    isConsent: item.isConsent,
  };
}
